import { DateTime } from 'luxon';
import { COMMUNICABLE_TYPES } from '../defaults';
import { ApScheduledStatus } from '../enums/ApScheduledStatus';
import { FieldDataType } from '../enums/FieldDataType';
import { FieldValidationType } from '../enums/FieldValidationType';
import { ChannelItemUnion } from '../types/ChannelItem';
import { ContactField } from '../types/ContactField';
import { ConversationGridFiltersItem } from '../types/ConversationGridFiltersItem';
import {
    ExtendsApSendStatus,
    ExtendsContactId,
    ExtendsFieldDataType,
    ExtendsFieldId,
    ExtendsFieldValue,
    ExtendsFilterId,
    ExtendsIsActive,
    ExtendsLeadId,
    ExtendsListId,
    ExtendsListType,
    ExtendsNoteId,
    ExtendsStartEndDate,
    ExtendsStatusId,
    ExtendsTagId,
    ExtendsTagText,
    ExtendsUserName,
    ExtendsValidationType,
    ExtendsViewId,
} from '../types/generics';
import { ListType } from '../types/Lists';
import { stringifyContactAddress } from './contactFieldHelpers';

/**
 * hasMatchingFieldId
 *
 * @description
 * Filter that returns true if the value of or a value in the fieldId argument matches
 * the WithFieldId object's fieldId property
 *
 * @param fieldId value or array of values to compare
 */
export const hasMatchingFieldId =
    (fieldId: number | number[]) =>
    <T extends ExtendsFieldId>(fieldItem: T): boolean =>
        Array.isArray(fieldId)
            ? fieldId.includes(fieldItem.fieldId)
            : fieldId === fieldItem.fieldId;

/**
 * hasDistinctFieldId
 *
 * @description
 * Filter that returns true if the value of or a value in the fieldId argument does not
 * match the ExtendsFieldId object's fieldId property
 *
 * @param fieldId value or array of values to compare
 */
export const hasDistinctFieldId =
    (fieldId: number | number[]) =>
    <T extends ExtendsFieldId>(fieldItem: T): boolean =>
        Array.isArray(fieldId)
            ? !fieldId.includes(fieldItem.fieldId)
            : fieldId !== fieldItem.fieldId;

/**
 * hasMatchingListId
 *
 * @description
 * Filter that returns true if the value of or a value in the listId argument matches
 * the ExtendsListId object's listId property
 *
 * @param listId value or array of values to compare
 */
export const hasMatchingListId =
    (listId: number | number[]) =>
    <T extends ExtendsListId>(listItem: T): boolean =>
        Array.isArray(listId) ? listId.includes(listItem.listId) : listId === listItem.listId;

/**
 * hasDistinctListId
 *
 * @description
 * Filter that returns true if the value of or a value in the listId argument does not
 * match the ExtendsListId object's listId property
 *
 * @param listId value or array of values to compare
 */
export const hasDistinctListId =
    (listId: number | number[]) =>
    <T extends ExtendsListId>(listItem: T): boolean =>
        Array.isArray(listId) ? !listId.includes(listItem.listId) : listId !== listItem.listId;

/**
 * hasMatchingListType
 *
 * @description
 * Filter that returns true if the value of or a value in the listType argument matches
 * the ExtendsListType object's type property
 */
export const hasMatchingListType =
    (listType: ListType | ListType[]) =>
    <T extends ExtendsListType>(list: T): boolean =>
        Array.isArray(listType) ? listType.includes(list.type) : list.type === listType;

/**
 * hasMatchingLeadId
 *
 * @description
 * Filter that returns true if the value of or a value in the leadId argument matches
 * the ExtendsLeadId object's leadId property
 *
 * @param leadId value or array of values to compare
 */
export const hasMatchingLeadId =
    (leadId: number | number[]) =>
    <T extends ExtendsLeadId>(contact: T): boolean =>
        Array.isArray(leadId) ? leadId.includes(contact.leadId) : leadId === contact.leadId;

/**
 * hasDistinctLeadId
 *
 * @description
 * Filter that returns true if the value of or a value in the leadId argument does not
 * match the ExtendsLeadId object's leadId property
 *
 * @param leadId value or array of values to compare
 */
export const hasDistinctLeadId =
    (contactId: number | number[]) =>
    <T extends ExtendsContactId>(contact: T): boolean =>
        Array.isArray(contactId)
            ? !contactId.includes(contact.contactId)
            : contactId !== contact.contactId;

/**
 * hasMatchingContactId
 *
 * @description
 * Filter that returns true if the value of or a value in the contactId argument matches
 * the ExtendsContactId object's contactId property
 */
export const hasMatchingContactId =
    (contactId: number | number[]) =>
    <T extends ExtendsContactId>(contact: T): boolean =>
        Array.isArray(contactId)
            ? contactId.includes(contact.contactId)
            : contactId === contact.contactId;

/**
 * hasDistinctContactId
 *
 * @description
 * Filter that returns true if the value of or a value in the contactId argument does not
 * match the ExtendsContactId object's contactId property
 */
export const hasDistinctContactId =
    (contactId: number | number[]) =>
    <T extends ExtendsContactId>(contact: T): boolean =>
        Array.isArray(contactId)
            ? !contactId.includes(contact.contactId)
            : contactId !== contact.contactId;

/**
 * hasMatchingViewId
 *
 * @description
 * Filter that returns true if the value of or a value in the viewId argument matches
 * the ExtendsViewId object's viewId property
 *
 * @param viewId value or array of values to compare
 */
export const hasMatchingViewId =
    (viewId: number | number[]) =>
    <T extends ExtendsViewId>(view: T): boolean =>
        Array.isArray(viewId) ? viewId.includes(view.viewId) : viewId === view.viewId;

/**
 * hasDistinctViewId
 *
 * @description
 * Filter that returns true if the value of or a value in the viewId argument does not
 * match the ExtendsViewId object's viewId property
 *
 * @param viewId value or array of values to compare
 */
export const hasDistinctViewId =
    (viewId: number | number[]) =>
    <T extends ExtendsViewId>(view: T): boolean =>
        Array.isArray(viewId) ? !viewId.includes(view.viewId) : viewId !== view.viewId;

/**
 * hasMatchingUserName
 *
 * @description
 * Filter that returns true if the value of or a value in the userName argument matches
 * the ExtendsUserName object's userName property
 */
export const hasMatchingUserName =
    (userName: string) =>
    <T extends ExtendsUserName>(user: T): boolean =>
        Array.isArray(userName) ? userName.includes(user.userName) : userName === user.userName;

/**
 * hasMatchingValidationType
 *
 * @description
 * Filter that returns true if the value of or a value in the type argument matches
 * the ExtendsValidationType object's validationType property
 *
 * @param type value or values to compare
 */
export const hasMatchingValidationType =
    (type: FieldValidationType | FieldValidationType[]) =>
    <T extends ExtendsValidationType>(field: T): boolean =>
        Array.isArray(type) ? type.includes(field.validationType) : type === field.validationType;

/**
 * hasValidGridFiltersValue
 *
 * @description
 * Determines whether or not filter group object should be considered valid.
 *
 * IMPORTANT: On its own should not be relied upon as input field validation/ sanitization.
 *
 * @param gridFilterItem
 */
export const hasValidGridFiltersValue = (gridFilterItem: ConversationGridFiltersItem) =>
    gridFilterItem.value.toString().trim().length > 0 || gridFilterItem.operand === 'is not empty';

/**
 * hasPopulatedFieldValue
 *
 * @description
 * Determines if the value property on an ExtendsFieldValue object has a value which is empty
 * or filled.
 *
 * IMPORTANT: On its own should not be relied upon as input field validation/ sanitization.
 *
 * @param field
 */
export const hasPopulatedFieldValue = <T extends ExtendsFieldValue>(field: T): boolean => {
    /* added to accomodate potential of "null" as value in create contact form date fields */
    if (!field.value) return false;

    return typeof field.value === 'object'
        ? stringifyContactAddress(field.value).trim().length > 0
        : field.value.length > 0;
};

/**
 * hasApSendStatus
 *
 * @description
 * Determines if sendStatus property of ExtendsApSendStatus matches the checked-for value
 *
 * @param statusAs
 */
export const hasApSendStatus =
    (statusAs: ApScheduledStatus | ApScheduledStatus[]) =>
    <T extends ExtendsApSendStatus>(statusObj: T): boolean =>
        Array.isArray(statusAs)
            ? statusAs.includes(statusObj.sendStatus)
            : statusObj.sendStatus === statusAs;

/**
 * hasMatchingStatusId
 *
 * @description
 * Filter that returns true if the value of or a value in the statusId argument matches
 * the ExtendsStatusId object's statusId property
 *
 * @param statusId
 */
export const hasMatchingStatusId =
    (statusId: number | number[]) =>
    <T extends ExtendsStatusId>(status: T): boolean =>
        Array.isArray(statusId) ? statusId.includes(status.statusId) : statusId === status.statusId;

/**
 * hasDistinctStatusId
 *
 * @description
 * Filter that returns true if the value of or a value in the statusId argument does not
 * match the ExtendsStatusId object's statusId property
 */
export const hasDistinctStatusId =
    (statusId: number | number[]) =>
    <T extends ExtendsStatusId>(status: T): boolean =>
        Array.isArray(statusId)
            ? !statusId.includes(status.statusId)
            : statusId !== status.statusId;

/**
 * hasMatchingTagId
 *
 * @description
 * Filter that returns true if the value of or a value in the tagId argument matches
 * the ExtendsTagId object's tagId property
 */
export const hasMatchingTagId =
    (tagId: number | number[]) =>
    <T extends ExtendsTagId>(tag: T): boolean =>
        Array.isArray(tagId) ? tagId.includes(tag.tagId) : tagId === tag.tagId;

/**
 * hasDistinctTagId
 *
 * @description
 * Filter that returns true if the value of or a value in the tagId argument does not
 * match the ExtendsTagId object's tagId property
 */
export const hasDistinctTagId =
    (tagId: number | number[]) =>
    <T extends ExtendsTagId>(tag: T): boolean =>
        Array.isArray(tagId) ? !tagId.includes(tag.tagId) : tagId !== tag.tagId;

/**
 * hasSearchedTagText
 *
 * @description
 * Returns true if the search keyword is found within the tag property of the ExtendsTagText
 * object
 */
export const hasSearchedTagText = <T extends ExtendsTagText>(searchKeyword: string) => {
    /*
        TIP: assign the regexp at this level to prevent re-assigned on every 
        iteration when passed to Array.filter()
     */
    const searchValues = searchKeyword.split(' ').join('|');
    const regexp = new RegExp(searchValues, 'gi');

    return (tagObject: T): boolean => tagObject.tag.search(regexp) > -1;
};

/**
 * hasMatchingNoteId
 *
 * @description
 * Filter that returns true if the value of or a value in the noteId argument matches
 * the ExtendsNoteId object's noteId property
 */
export const hasMatchingNoteId =
    (noteId: number | number[]) =>
    <T extends ExtendsNoteId>(note: T): boolean =>
        Array.isArray(noteId) ? noteId.includes(note.noteId) : noteId === note.noteId;

/**
 * hasDistinctNoteId
 *
 * @description
 * Filter that returns true if the value of or a value in the noteId argument does not
 * match the ExtendsNoteId object's noteId property
 */
export const hasDistinctNoteId =
    (noteId: number | number[]) =>
    <T extends ExtendsNoteId>(note: T): boolean =>
        Array.isArray(noteId) ? !noteId.includes(note.noteId) : noteId !== note.noteId;

/**
 * hasDistinctFilterId
 *
 * @description
 * Filter that returns true if the value of or a value in the filterId argument does not
 * match the ExtendsfilterId object's filterId property
 */
export const hasDistinctFilterId =
    (filterId: number | number[]) =>
    <T extends ExtendsFilterId>(filter: T): boolean =>
        Array.isArray(filterId) ? !filterId.includes(filter.id!) : filterId !== filter.id;

/**
 * hasEmptyDeviceId
 *
 * @description
 * Returns true if the provided media device has an Id that is not en empty string
 */
export const hasDeviceIdAsString = (mediaDevice: MediaDeviceInfo): boolean =>
    mediaDevice.deviceId.length > 0;

/**
 * hasIsActiveAsTrue
 *
 * @description
 * Returns true if ExtendsIsActive object has isActive property with true boolean value
 */
export const hasIsActiveAsTrue = <T extends ExtendsIsActive>(object: T): boolean =>
    object.isActive === true ? true : false;

export const isCommunicableEvent = (channelItem: ChannelItemUnion) => {
    return COMMUNICABLE_TYPES.includes(channelItem.subType);
};

export const isCommunicableField = (field: ContactField) => {
    return (
        field.validationType === FieldValidationType.email ||
        field.validationType === FieldValidationType.usMobilePhone
    );
};

/**
 * isCalendarEventToday
 *
 * @description
 * Returns true if the calendar event occurs within "Today"
 */
export const isCalendarEventToday = <T extends ExtendsStartEndDate>(object: T): boolean => {
    return (
        object.startDate.hasSame(DateTime.local(), 'day') ||
        (!!object.endDate && object.endDate.hasSame(DateTime.local(), 'day'))
    );
};

/**
 * isCalendarEventNext7Days
 *
 * @description
 * Returns true if the calendar event occurs within "Next 7 Days"
 */
export const isCalendarEventNext7Days = <T extends ExtendsStartEndDate>(object: T): boolean => {
    const startDiff = object.startDate.diffNow('days').days;
    const endDiff = object?.endDate?.diffNow('days').days;

    return (startDiff >= 0 && startDiff <= 7) || (!!endDiff && endDiff >= 0 && endDiff <= 7);
};

/**
 * isCalendarEventNext30Days
 *
 * @description
 * Returns true if the calendar event occurs within "Next 30 Days"
 */
export const isCalendarEventNext30Days = <T extends ExtendsStartEndDate>(object: T): boolean => {
    const startDiff = object.startDate.diffNow('days').days;
    const endDiff = object?.endDate?.diffNow('days').days;

    return (startDiff >= 0 && startDiff <= 30) || (!!endDiff && endDiff >= 0 && endDiff <= 30);
};

/**
 * isCalendarEventFuture
 *
 * @description
 * Returns true if the calendar event occurs in the future
 */
export const isCalendarEventFuture = <T extends ExtendsStartEndDate>(object: T): boolean => {
    const startDiff = object.startDate.diffNow('days').days;
    const endDiff = object.endDate?.diffNow('days').days;

    return startDiff >= 0 || (!!endDiff && endDiff >= 0);
};

/**
 * isCalendarEventPast7Days
 *
 * @description
 * Returns true if the calendar event occurs within "Past 7 Days"
 */
export const isCalendarEventPast7Days = <T extends ExtendsStartEndDate>(object: T): boolean => {
    const startDiff = object.startDate.diffNow('days').days;
    const endDiff = object.endDate?.diffNow('days').days;

    return (startDiff <= 0 && startDiff >= -7) || (!!endDiff && endDiff <= 0 && endDiff >= -7);
};

/**
 * isCalendarEventPast30Days
 *
 * @description
 * Returns true if the calendar event occurs within "Past 30 Days"
 */
export const isCalendarEventPast30Days = <T extends ExtendsStartEndDate>(object: T): boolean => {
    const startDiff = object.startDate.diffNow('days').days;
    const endDiff = object.endDate?.diffNow('days').days;

    return (startDiff <= 0 && startDiff >= -30) || (!!endDiff && endDiff <= 0 && endDiff >= -30);
};

/**
 * isCalendarEventPast
 *
 * @description
 * Returns true if the calendar event occurs in the future
 */
export const isCalendarEventPast = <T extends ExtendsStartEndDate>(object: T): boolean => {
    const startDiff = object.startDate.diffNow('days').days;
    const endDiff = object.endDate?.diffNow('days').days;

    return startDiff <= 0 || (!!endDiff && endDiff <= 0);
};

export const enumToKey = (anEnum: object, instance: string) => {
    const found = Object.entries(anEnum).find(([key, val]) => instance === (val as string));
    return (found && (found[0] as string)) || '';
};

/**
 * hasMatchingFieldDataType
 *
 * @description
 * Filter that returns true if the value of or a value in the type argument matches
 * the ExtendsFieldDataType object's fieldType property
 */
export const hasMatchingFieldDataType =
    (type: FieldDataType | FieldDataType[]) =>
    <T extends ExtendsFieldDataType>(field: T): boolean =>
        Array.isArray(type) ? type.includes(field.fieldType) : type === field.fieldType;
